import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, publicItemImage, avatarUrl } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, BellRing, Inbox, MessageCircle, Scale, Check, Tag, ArrowLeftRight, XCircle, CheckCircle2 } from "lucide-react";
import { timeAgo } from "@/components/Social";

const POLL_MS = 30000;

// Colours as meaning: wait=amber (needs you), good=green (accepted/fair), bad=rose (declined), info=sky (fyi), action=lime (opportunity)
const TYPE_META = {
  offer_created: { icon: Inbox, tone: "wait", label: "New offer" },
  offer_message: { icon: MessageCircle, tone: "info", label: "Message" },
  offer_counter: { icon: Tag, tone: "wait", label: "Counter-offer" },
  offer_accepted: { icon: CheckCircle2, tone: "good", label: "Deal" },
  offer_declined: { icon: XCircle, tone: "bad", label: "Declined" },
  offer_withdrawn: { icon: XCircle, tone: "off", label: "Withdrawn" },
  trade_match_new: { icon: Scale, tone: "action", label: "Trade match" },
  follow: { icon: ArrowLeftRight, tone: "info", label: "New follower" },
};
const TONE_BG = { wait: "bg-wait text-wait-foreground", good: "bg-good text-good-foreground", bad: "bg-bad text-bad-foreground", info: "bg-info text-info-foreground", action: "bg-action text-action-foreground", off: "bg-off text-off-foreground" };

export const useNotifications = () => {
  const [count, setCount] = useState(0);
  const [pulse, setPulse] = useState(false);
  const prev = useRef(0);
  const refresh = useCallback(async () => {
    try {
      const r = await api.notificationsUnread();
      if (r.unread > prev.current) { setPulse(true); setTimeout(() => setPulse(false), 600); }
      prev.current = r.unread; setCount(r.unread);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    const onVis = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [refresh]);
  return { count, pulse, refresh, setCount };
};

const Row = ({ n, onOpen }) => {
  const meta = TYPE_META[n.type] || { icon: Bell, tone: "info", label: "Update" };
  const Icon = meta.icon;
  return (
    <button onClick={() => onOpen(n)} className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-secondary/70 transition-colors ${n.read_at ? "" : "bg-secondary/40"}`} data-testid="notification-row" data-type={n.type}>
      <div className="relative shrink-0">
        {n.thumb_item_id ? <img src={publicItemImage({ id: n.thumb_item_id, updated_at: n.created_at })} alt="" className="h-11 w-9 rounded-md object-cover bg-secondary" />
          : <Avatar className="h-10 w-10 text-xs">{n.actor?.avatar_public && <AvatarImage src={avatarUrl(n.actor.handle)} alt="" />}<AvatarFallback className="bg-secondary">{(n.actor?.handle || "S").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>}
        <span className={`absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-popover ${TONE_BG[meta.tone]}`}><Icon className="h-3 w-3" /></span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium truncate">{n.title}</p>
          <span className="font-mono-plex text-[10px] text-muted-foreground shrink-0">{timeAgo(n.created_at)}</span>
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
      </div>
      {!n.read_at && <span className={`mt-2 meaning-dot ${TONE_BG[meta.tone].split(" ")[0]}`} data-testid="notification-unread-dot" />}
    </button>
  );
};

export const NotificationsBell = () => {
  const navigate = useNavigate();
  const { count, pulse, refresh, setCount } = useNotifications();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    api.notifications().then((r) => setItems(r.items)).catch(() => setItems([]));
  }, [open]);

  const openRow = async (n) => {
    if (!n.read_at) { api.notificationsRead([n.id]).then(refresh).catch(() => {}); }
    setOpen(false);
    if (n.type === "trade_match_new") navigate(n.my_item_id ? `/market?tab=browse&match_for=${n.my_item_id}` : "/social?tab=market");
    else if (n.type === "follow") navigate(n.actor?.handle ? `/u/${n.actor.handle}` : "/social?tab=people");
    else if (n.type === "follow_listing_fit" || n.type === "follow_listing") navigate(n.actor?.handle ? `/u/${n.actor.handle}` : "/market?tab=foryou");
    else navigate(`/market?tab=offers&box=${n.box || "inbox"}&open=${n.offer_id || ""}`);
  };
  const markAll = async () => {
    try { await api.notificationsRead("all"); setItems((l) => (l || []).map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() }))); setCount(0); } catch { /* ignore */ }
  };

  const Icon = count > 0 ? BellRing : Bell;
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors" data-testid="notifications-bell-button" aria-label={count ? `${count} unread notifications` : "Notifications"}>
          <Icon className={`h-5 w-5 ${count > 0 ? "text-foreground" : ""}`} />
          {count > 0 && <span className={`absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-action text-action-foreground text-[10px] font-bold leading-[18px] text-center ${pulse ? "animate-pulse-once" : ""}`} data-testid="notifications-unread-badge">{count > 99 ? "99+" : count}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] max-w-[92vw] p-2 glass" data-testid="notifications-dropdown">
        <div className="flex items-center justify-between px-2 py-1.5">
          <p className="font-display text-lg">Notifications</p>
          <Button variant="ghost" size="sm" onClick={markAll} disabled={!count} className="h-7 text-xs rounded-full" data-testid="notifications-mark-all-read"><Check className="h-3.5 w-3.5 mr-1" /> Mark all read</Button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto space-y-0.5">
          {items === null ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)
            : items.length === 0 ? <div className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">You're all caught up. Offers, messages and new trade matches land here.</div>
            : items.map((n) => <Row key={n.id} n={n} onOpen={openRow} />)}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
